import type { components } from '@connected/api-client/api.generated';

import { api } from '@connected/api-client';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

export function useDeleteDistributionBoardMutation({
    electricalProjectVersionId,
}: {
    electricalProjectVersionId: string;
}) {
    const { t: c } = useTranslation('common');

    const deleteDistributionBoardMutation = api.useMutation(
        'delete',
        '/api/v2/electrical/projects/distributionboards/delete',
        {
            onMutate: async (variable) => {
                const distributionBoardQueryOptions = elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({ electricalProjectVersionId });
                const previousDataList = queryClient.getQueryData(distributionBoardQueryOptions.queryKey) as components['schemas']['ListElectricalProjectDistributionBoardQueryResult'];
                const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
                    electricalProjectVersionId,
                });

                await queryClient.cancelQueries(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({ electricalProjectVersionId }));
                await queryClient.cancelQueries(summaryOptions);

                queryClient.setQueryData<components['schemas']['ListElectricalProjectDistributionBoardQueryResult']>(
                    elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({ electricalProjectVersionId }).queryKey,
                    (old) => {
                        if (old) {
                            const updatedData: components['schemas']['ListElectricalProjectDistributionBoardQueryResult'] = {
                                ...old,
                                DistributionBoards: old.DistributionBoards.filter(
                                    x => x.ElectricalProjectDistributionBoardId !== variable.params.query.ElectricalProjectDistributionBoardId,
                                ),
                            };
                            return updatedData;
                        }
                        return old;
                    },
                );

                queryClient.setQueryData<components['schemas']['ViewElectricalProjectSummaryQueryResult']>(
                    summaryOptions.queryKey,
                    (old) => {
                        if (old && old.Summary) {
                            return {
                                ...old,
                                Summary: {
                                    ...old.Summary,
                                    ElectricalProjectVersionId: old.Summary.ElectricalProjectVersionId,
                                    DistributionBoardCount: (old.Summary.DistributionBoardCount || 1) - 1,
                                },
                            };
                        }
                        return old;
                    },
                );

                return { previousDataList };
            },
            onSuccess: () => {
                toast.success(c('messages.toast.distributionBoardDeleted'));
            },
            onSettled: async () => {
                await queryClient.invalidateQueries(
                    elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({ electricalProjectVersionId }),
                );
            },
            scope: {
                id: electricalProjectVersionId,
            },
        },
    );

    async function handleDelete({
        electricalProjectDistributionBoardId,
    }: {
        electricalProjectDistributionBoardId: string;
    }) {
        if (await showConfirm(c('messages.confirmDelete'))) {
            deleteDistributionBoardMutation.mutate({
                params: {
                    query: {
                        ElectricalProjectDistributionBoardId:
                            electricalProjectDistributionBoardId,
                    },
                },
            });
        }
    }

    return {
        deleteDistributionBoardMutation,
        handleDelete,
    };
}
